"use client";

import { useTranslations } from "next-intl";

import type { FunctionTestIssue, ValueFound, ValueRefusal } from "../../lib/function-value";

/**
 * A value the judge refuses, in the reader's language - `EX-FNC04`. The port
 * in `function-value.ts` answers a code and what it names; these say it as a
 * sentence an author (the test editor) or a student (the run panel,
 * `EX-FNC05`) can act on.
 */
export function useValueRefusalText(): (refusal: ValueRefusal) => string {
  const t = useTranslations("judge.function");
  const foundText = (found: ValueFound) =>
    found.kind === "number"
      ? t("found_number", { text: found.text })
      : found.kind === "bool"
        ? t("found_bool", { text: found.text })
        : t(`found_${found.kind}`);
  return (refusal) => {
    switch (refusal.code) {
      case "not_a_number":
      case "not_a_value":
      case "list_comma":
        return t(`value_${refusal.code}`, { character: refusal.character });
      case "bad_escape":
        return t("value_bad_escape", { escape: refusal.escape });
      case "trailing":
        return t("value_trailing", { rest: refusal.rest });
      case "not_list":
        return t("value_not_list", { type: refusal.type, found: foundText(refusal.found) });
      case "not_whole":
        return t("value_not_whole", { type: refusal.type, found: foundText(refusal.found) });
      case "not_double":
      case "not_bool":
      case "not_char":
      case "not_string":
        return t(`value_${refusal.code}`, { found: foundText(refusal.found) });
      case "int_range":
      case "long_range":
      case "double_range":
        return t(`value_${refusal.code}`, { text: refusal.text });
      default:
        return t(`value_${refusal.code}`);
    }
  };
}

/** A whole test file that does not fit the signature, named the way an author fixes it. */
export function useFunctionTestIssueText(): (issue: FunctionTestIssue) => string {
  const t = useTranslations("judge.function");
  const refusalText = useValueRefusalText();
  return (issue) => {
    if ("refusal" in issue) {
      return issue.stream === "input"
        ? t("issue_input", { line: issue.line, param: issue.param, reason: refusalText(issue.refusal) })
        : t("issue_expected", { line: issue.line, reason: refusalText(issue.refusal) });
    }
    switch (issue.code) {
      case "no_calls":
        return t("issue_no_calls");
      case "short_call":
        return t("issue_short_call", { param: issue.param, width: issue.width });
      case "count":
        return t("issue_count", { calls: issue.calls, answers: issue.answers });
    }
  };
}
